import { PortalDialogProps } from "@app/services/modal.service";
import Modal from "../modal/modal.component";
import Button from "../button";
import {
  getContractStatusLabel,
  ContractStatus,
  ContractRequestStatus,
} from "@app/constants";
import BookService from "@app/services/http/book.service";
import { addToast } from "../toast/toast.service";
import { Environment } from "@environments/environment";

interface ContractStatusModalProps {
  bookId: number;
  contractStatus: number;
  contractRequestStatus?: number;
  contractLink?: string | null;
  refreshCallback?: () => void;
}

function ContractStatusModal({
  portalDialogRef,
  portalData,
}: PortalDialogProps<ContractStatusModalProps>) {
  const {
    bookId,
    contractStatus,
    contractRequestStatus = 0,
    contractLink,
    refreshCallback,
  } = portalData || {};

  const getHintText = (status: number) => {
    switch (status) {
      case ContractStatus.NO_CONTRACT:
        return "Bạn cần cập nhật hợp đồng để có thể tiến hành xin giấy phép xuất bản";
      case ContractStatus.PENDING_APPROVAL:
        return "Vui lòng chờ khoảng 7-15 ngày để tiến hành xin giấy phép (lúc này BOOKAS phải nộp đơn đăng ký rồi)";
      case ContractStatus.FILING_PROCESS:
        return "Trong thời gian chờ có thể viết và đăng tải thêm";
      case ContractStatus.NEED_SUPPLEMENT:
        return "Bạn cần cập nhật hợp đồng để có thể tiến hành xin giấy phép xuất bản";
      case ContractStatus.COMPLETED:
        return "Tác phẩm được duyệt (Rất tuyệt, tác phẩm của bạn đã được thông qua kiểm duyệt bởi BOOKAS)";
      default:
        return "Tác phẩm được duyệt (Rất tuyệt, tác phẩm của bạn đã được thông qua kiểm duyệt bởi BOOKAS)";
    }
  };

  const shouldShowUpdateButton = () => {
    return (
      contractStatus === ContractStatus.NO_CONTRACT ||
      contractStatus === ContractStatus.NEED_SUPPLEMENT
    );
  };

  const shouldShowApproveButton = () => {
    return contractStatus !== ContractStatus.COMPLETED;
  };

  const isApproveButtonDisabled = () => {
    return contractRequestStatus === ContractRequestStatus.REQUESTED;
  };

  const getApproveButtonText = () => {
    return contractRequestStatus === ContractRequestStatus.REQUESTED
      ? "Đã gửi duyệt hợp đồng"
      : "Gửi duyệt hợp đồng";
  };

  const handleUpdateContract = () => {
    const contractUrl = Environment.BOOKAS_CONTRACT_URL || "";

    if (contractUrl) {
      window.open(contractUrl, "_blank");
    }
  };

  const handleApproveContract = async () => {
    if (!bookId) {
      addToast({
        text: "Không tìm thấy thông tin sách",
        status: "inValid",
      });

      return;
    }

    try {
      await BookService.approveContract(bookId);
      addToast({
        text: "Đã gửi yêu cầu duyệt hợp đồng thành công",
        status: "valid",
      });
      portalDialogRef.close();
      refreshCallback?.();
    } catch (error) {
      addToast({
        text: "Có lỗi xảy ra khi gửi yêu cầu duyệt hợp đồng",
        status: "inValid",
      });
    }
  };

  return (
    <Modal onCancel={portalDialogRef.close} buttonCancelInChildren={true}>
      <div className="w-[600px] py-7 px-6">
        <div className="text-xl text-center font-semibold mb-6 text-gray-800">
          Thông tin trạng thái hợp đồng
        </div>

        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 mb-3">
            Trạng thái hiện tại:
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-lg font-medium text-blue-800 mb-2">
              {getContractStatusLabel(contractStatus)}
            </div>
            <div className="text-sm text-blue-600">
              {getHintText(contractStatus || 1)}
            </div>
          </div>
        </div>

        {contractLink && contractLink.trim() && (
          <div className="mb-6">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Link hợp đồng:
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <a
                href={contractLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline break-all text-sm"
              >
                {contractLink}
              </a>
            </div>
          </div>
        )}

        <div className="flex justify-center gap-3">
          {shouldShowUpdateButton() && (
            <Button
              size="xs"
              label="Cập nhật hợp đồng"
              width={180}
              className="!h-[48px] !leading-[48px]"
              onClick={handleUpdateContract}
            />
          )}
          {shouldShowApproveButton() && (
            <Button
              size="xs"
              label={getApproveButtonText()}
              width={180}
              className={`!h-[48px] !leading-[48px] ${
                isApproveButtonDisabled()
                  ? "!bg-gray-400 !text-gray-600 cursor-not-allowed"
                  : ""
              }`}
              onClick={handleApproveContract}
              disabled={isApproveButtonDisabled()}
            />
          )}
          <Button
            size="xs"
            label="Đóng"
            theme="secondary"
            width={120}
            className="!h-[48px] !leading-[48px]"
            onClick={portalDialogRef.close}
          />
        </div>
      </div>
    </Modal>
  );
}

export default ContractStatusModal;
